import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref, computed } from 'vue'
import { TransitionPresets, useTransition } from '@vueuse/core'
import { getMetrics } from '@/api/stats.js'
import { getMeta } from '@/api/metadata.js'
import { useSession } from './session'
import {
  annotationLabelStyle,
  barChartOptions,
  formatCount,
} from '@/utils/charts.js'
import { chartColors } from '@/constants'
import {
  SEARCH_MERGE_CHART_KEY,
  foldSearchMerge,
  dupeSetChartBars,
  searchMergeChartDirector,
} from '@/utils/search-merge'
export const useStats = defineStore('stats', () => {
  const sessionStore = useSession()

  const databaseIsClean = ref(false)

  const isLoadingMetadata = ref(false)
  const isMetadataError = ref(false)
  const issuesMetadata = ref(
    JSON.parse(localStorage.getItem('dashboardMetadata')),
  )

  const issuesMetrics = ref(null)
  const isLoadingIssuesStats = ref(false)
  const isIssuesStatsError = ref(false)

  const fixedRecordsCount = ref(0)
  const fixedRecordsCountAnimated = useTransition(fixedRecordsCount, {
    duration: 1000,
    transition: TransitionPresets.easeInOutCubic,
  })

  const fixErrorsCount = ref(0)
  const fixErrorsCountAnimated = useTransition(fixErrorsCount, {
    duration: 1000,
    transition: TransitionPresets.easeInOutCubic,
  })

  const undoFixErrorsCount = ref(0)
  const undoFixErrorsCountAnimated = useTransition(undoFixErrorsCount, {
    duration: 1000,
    transition: TransitionPresets.easeInOutCubic,
  })

  const duplicatesCount = ref(0)
  const duplicatesCountAnimated = useTransition(duplicatesCount, {
    duration: 1000,
    transition: TransitionPresets.easeInOutCubic,
  })

  const duplicateRecordsCount = ref(0)
  const duplicateRecordsCountAnimated = useTransition(duplicateRecordsCount, {
    duration: 1000,
    transition: TransitionPresets.easeInOutCubic,
  })

  // false until the backend reports open.record_count for every rule; the
  // duplicates widget then falls back to set counts rather than plotting
  // empty bars for the rules that lack one
  const hasDuplicateRecordCounts = computed(
    () =>
      issuesMetrics.value?.duplicateSetsToReview?.every(
        item => item.recordCount !== null && item.recordCount !== undefined,
      ) ?? false,
  )

  const duplicateRecordsPercentage = computed(() => {
    const total = issuesMetrics.value?.totalRecords
    if (!total || !hasDuplicateRecordCounts.value) return null
    // a record matched by two rules is counted once per rule, so the sum can
    // exceed the org total; cap it so the tile never reads over 100%
    return Math.min((duplicateRecordsCountAnimated.value / total) * 100, 100)
  })

  const mergedDuplicatesCount = ref(0)
  const mergedDuplicatesCountAnimated = useTransition(mergedDuplicatesCount, {
    duration: 1000,
    transition: TransitionPresets.easeInOutCubic,
  })

  const queuedDuplicatesCount = ref(0)
  const queuedDuplicatesCountAnimated = useTransition(queuedDuplicatesCount, {
    duration: 1000,
    transition: TransitionPresets.easeInOutCubic,
  })

  const queuedDataIssuesCount = ref(0)
  const queuedDataIssuesCountAnimated = useTransition(queuedDataIssuesCount, {
    duration: 1000,
    transition: TransitionPresets.easeInOutCubic,
  })

  const queuedLastUpdated = ref(null)
  let queuedPollingInterval = null

  const mergeErrorsCount = ref(0)
  const mergeErrorsCountAnimated = useTransition(mergeErrorsCount, {
    duration: 1000,
    transition: TransitionPresets.easeInOutCubic,
  })

  const undoMergeErrorsCount = ref(0)
  const undoMergeErrorsCountAnimated = useTransition(undoMergeErrorsCount, {
    duration: 1000,
    transition: TransitionPresets.easeInOutCubic,
  })

  const metadataPromise = ref(null)

  const FALLBACK_COLOR = '#9E9E9E'

  // beyond this many rules the vertical bars are too narrow for their labels,
  // so the widget switches to the horizontal list
  const DUPLICATE_RULES_CHART_MAX = 7

  // point annotations are used only to place text, never a visible marker.
  // built per annotation so ApexCharts can never mutate a shared object
  function hiddenAnnotationMarker() {
    return {
      size: 0,
      strokeWidth: 0,
      fillColor: 'transparent',
      strokeColor: 'transparent',
    }
  }

  // ApexCharts matches a point annotation to a bar by its category text, so
  // rule names are not guaranteed unique (they are only validated non-empty).
  // Repeats get trailing zero-width spaces: invisible on the axis, but enough
  // for the annotation lookup to resolve to the right bar
  function disambiguateChartLabel(label, seenCount) {
    if (seenCount === 0) return label
    const padding = '\u200B'.repeat(seenCount)
    return Array.isArray(label)
      ? [...label.slice(0, -1), label[label.length - 1] + padding]
      : label + padding
  }

  function validateDirector(directorId) {
    if (directorId === SEARCH_MERGE_CHART_KEY) {
      return searchMergeChartDirector(issuesMetadata.value.directors)
    }
    const director = issuesMetadata.value.directors?.[directorId]
    if (!director) {
      return { name: directorId, objects: [], _isFallback: true }
    }
    return director
  }

  function validateObject(objectId) {
    const object = issuesMetadata.value.objects?.[objectId]
    if (!object) {
      return {
        name: objectId,
        label: objectId,
        _isFallback: true,
      }
    }
    return object
  }

  function validateIssueType(issueId) {
    const issueType = issuesMetadata.value.issueTypes?.[issueId]
    if (!issueType) {
      return {
        label: issueId,
        color: FALLBACK_COLOR,
        _isFallback: true,
      }
    }
    return issueType
  }

  function directorObjects(director) {
    const objs = director.objects?.length > 0 ? director.objects : ['_unknown']
    return objs.map(validateObject)
  }

  // Manual, Automatic and Undo bars, the same in every rule's chart
  const TIER_COLORS = chartColors.slice(0, 3)

  // fixed by metadata order, the same order getMeta colors objects in
  function homeSlots(ids) {
    return new Map(ids.map((id, index) => [id, index % chartColors.length]))
  }

  // Search & Merge Directors only ever chart folded, as one last bar
  const ruleSlots = computed(() => {
    const directors = Object.entries(issuesMetadata.value?.directors ?? {})
    const slots = homeSlots(
      directors
        .filter(([, director]) => director.origin !== 'search_merge')
        .map(([id]) => id),
    )
    slots.set(SEARCH_MERGE_CHART_KEY, chartColors.length - 1)
    return slots
  })

  const objectSlots = computed(() =>
    homeSlots(Object.keys(issuesMetadata.value?.objects ?? {})),
  )

  // own color, unless the bar before has it: then one unlike both neighbours
  function barColors(ids, slots) {
    const homes = ids.map(id => slots.get(id) ?? 0)
    const assigned = []
    homes.forEach((home, index) => {
      const previous = assigned[index - 1]
      assigned[index] =
        home !== previous
          ? home
          : chartColors
              .map((_, step) => (home + step) % chartColors.length)
              .find(slot => slot !== previous && slot !== homes[index + 1])
    })
    return assigned.map(slot => chartColors[slot])
  }

  function ruleColors(items) {
    return barColors(
      items.map(item => item.director),
      ruleSlots.value,
    )
  }

  async function getMetadata() {
    if (metadataPromise.value) {
      return metadataPromise.value
    }

    isLoadingMetadata.value = true
    isMetadataError.value = false

    metadataPromise.value = getMeta()
      .then(metadata => {
        issuesMetadata.value = metadata
        localStorage.setItem('dashboardMetadata', JSON.stringify(metadata))
        return metadata
      })
      .catch(error => {
        isMetadataError.value = true
        throw error
      })
      .finally(() => {
        isLoadingMetadata.value = false
        metadataPromise.value = null
      })

    return metadataPromise.value
  }

  // Search & Merge creates a Director at its first merge for an object and
  // Layout, after the app has loaded the metadata, so its sets would chart
  // under a bare id. Reload only when the charts name a Director the metadata
  // doesn't know. The charts already work without it, so a failure here keeps
  // them as they are rather than raising the app-wide error.
  async function reloadMetadataForNewDirectors() {
    if (!sessionStore.searchMergeFlagEnabled || !issuesMetrics.value) return

    const known = issuesMetadata.value?.directors || {}
    const metrics = issuesMetrics.value
    const hasUnknownDirector = [
      metrics.mergedDuplicatesSets,
      metrics.queuedDuplicateSets,
      metrics.mergeErrors,
      metrics.undoMergeErrors,
    ]
      .flatMap(entries => entries || [])
      .some(entry => !known[entry.director])
    if (!hasUnknownDirector) return

    try {
      const metadata = await getMeta()
      issuesMetadata.value = metadata
      localStorage.setItem('dashboardMetadata', JSON.stringify(metadata))
    } catch {
      // Keep the metadata already loaded.
    }
  }

  const overviewChart = computed(() => {
    if (
      issuesMetrics.value &&
      Object.keys(issuesMetrics.value.overviewStats).length > 0
    ) {
      return {
        series: [
          issuesMetrics.value.overviewStats.clean,
          issuesMetrics.value.overviewStats.dirty,
        ],
        chartOptions: {
          chart: {
            height: 265,
            type: 'pie',
          },
          labels: ['Clean', 'Dirty'],
          colors: ['#224979', '#FCBB15'],
          legend: {
            position: 'bottom',
          },
        },
      }
    }

    return null
  })

  const hasDuplicateRuleWarning = computed(() => {
    if (!issuesMetadata.value || !issuesMetrics.value) return false

    return issuesMetrics.value.duplicateSetsToReview.some(item => {
      const director = validateDirector(item.director)
      if (director._isFallback) return true

      const directorObjects =
        director.objects?.length > 0 ? director.objects : ['_unknown']
      return directorObjects.some(el => validateObject(el)._isFallback)
    })
  })

  const duplicateSetsChart = computed(() => {
    if (!issuesMetadata.value || !issuesMetrics.value) {
      return null
    }

    //horizontal
    if (
      issuesMetrics.value.duplicateSetsToReview.length >
      DUPLICATE_RULES_CHART_MAX
    ) {
      return {
        hasWarning: hasDuplicateRuleWarning.value,
        chartOptions: { chart: { height: 265, type: 'bar' } },
      }
    }

    const categories = []
    const names = []
    // ApexCharts matches a point annotation to a category by joining
    // multi-line labels with a space, so mirror that normalization here
    const annotationX = []
    const records = []
    const sets = []
    const labelCounts = new Map()

    for (const item of issuesMetrics.value.duplicateSetsToReview) {
      const director = validateDirector(item.director)
      names.push(director.name)
      const wrappedLabel = ruleLabel(
        director.name,
        issuesMetrics.value.duplicateSetsToReview.length,
      )
      const labelKey = Array.isArray(wrappedLabel)
        ? wrappedLabel.join(' ')
        : wrappedLabel
      const seenCount = labelCounts.get(labelKey) ?? 0
      labelCounts.set(labelKey, seenCount + 1)
      const label = disambiguateChartLabel(wrappedLabel, seenCount)
      categories.push(label)
      annotationX.push(Array.isArray(label) ? label.join(' ') : label)
      records.push(item.recordCount || 0)
      sets.push(item.count || 0)
    }

    const showRecords = hasDuplicateRecordCounts.value
    const base = barChartOptions({
      categories,
      names,
      values: showRecords ? records : sets,
      unit: showRecords ? 'record' : 'set',
      sub: showRecords
        ? index => formatCount(sets[index], 'duplicate set')
        : undefined,
      colors: ruleColors(issuesMetrics.value.duplicateSetsToReview),
      testAttributes: {
        bars: issuesMetrics.value.duplicateSetsToReview.map((item, index) => ({
          'data-test-rule-id': item.director,
          'data-test-sets': sets[index],
          'data-test-records': records[index],
        })),
        labels: issuesMetrics.value.duplicateSetsToReview.map(item => ({
          'data-test-rule-id': item.director,
        })),
      },
    })

    const annotationPoints = recordsFontSize =>
      annotationX.flatMap((x, index) => {
        const y = showRecords ? records[index] : sets[index]
        const setsLabel = {
          x,
          y,
          seriesIndex: 0,
          marker: hiddenAnnotationMarker(),
          label: {
            text: formatCount(sets[index], 'set'),
            offsetY: showRecords ? -7 : -8,
            textAnchor: 'middle',
            borderWidth: 0,
            style: annotationLabelStyle({ muted: showRecords }),
          },
        }

        if (!showRecords) return [setsLabel]

        return [
          {
            x,
            y,
            seriesIndex: 0,
            marker: hiddenAnnotationMarker(),
            label: {
              text: formatCount(records[index], 'record'),
              offsetY: -22,
              textAnchor: 'middle',
              borderWidth: 0,
              style: {
                ...annotationLabelStyle(),
                color: '#304758',
                fontSize: recordsFontSize,
              },
            },
          },
          setsLabel,
        ]
      })

    return {
      hasWarning: hasDuplicateRuleWarning.value,
      // ApChartCard uses these when the card is too narrow for 12px record labels
      ...(annotationX.length >= DUPLICATE_RULES_CHART_MAX && {
        narrow: {
          below: 700,
          options: { annotations: { points: annotationPoints('11px') } },
        },
      }),
      series: [
        {
          name: showRecords ? 'Affected Records' : 'Duplicate Sets',
          data: showRecords ? records : sets,
        },
      ],
      chartOptions: {
        ...base,
        // both labels are drawn as point annotations (instead of a data label
        // for the records) so the two lines keep a fixed 16px gap and the
        // tallest bar's label is never clamped against the top of the chart
        annotations: { points: annotationPoints('12px') },
        dataLabels: {
          enabled: false,
        },
        // room for the stacked labels above the tallest bar
        grid: {
          ...base.grid,
          padding: {
            ...base.grid.padding,
            top: showRecords ? 36 : 20,
          },
        },
        responsive: [
          {
            breakpoint: 1000,
            options: {
              xaxis: {
                labels: {
                  style: {
                    fontSize: '10px',
                  },
                },
              },
            },
          },
          {
            breakpoint: 768,
            options: {
              xaxis: {
                labels: {
                  style: {
                    fontSize: '9px',
                  },
                },
              },
            },
          },
        ],
      },
    }
  })

  //horizontal
  const duplicateRuleRows = computed(() => {
    if (!issuesMetadata.value || !issuesMetrics.value) {
      return []
    }

    const showRecords = hasDuplicateRecordCounts.value
    const items = issuesMetrics.value.duplicateSetsToReview
    const colors = ruleColors(items)

    // same order as the vertical chart
    return items.map((item, index) => ({
      key: `${item.director}-${index}`,
      director: item.director,
      name: validateDirector(item.director).name,
      records: item.recordCount || 0,
      sets: item.count,
      value: showRecords ? item.recordCount || 0 : item.count,
      color: colors[index],
    }))
  })

  const hasDuplicateRuleList = computed(
    () => duplicateRuleRows.value.length > DUPLICATE_RULES_CHART_MAX,
  )

  // kept together so a caller cannot bind one without the other
  const duplicateRuleList = computed(() => ({
    rows: duplicateRuleRows.value,
    showRecords: hasDuplicateRecordCounts.value,
  }))

  // the card infers emptiness from the chart series, which the list never draws
  const hasDuplicateData = computed(() =>
    duplicateRuleRows.value.some(row => row.sets > 0 || row.records > 0),
  )

  // The duplicate-set chart lists with every Search & Merge Director folded
  // into one entry. The totals elsewhere read the unfolded lists: folding moves
  // counts between entries, never changes their sum.
  const dupeSetCharts = computed(() => {
    const directors = issuesMetadata.value?.directors
    const metrics = issuesMetrics.value
    return {
      mergedDuplicatesSets: foldSearchMerge(
        metrics.mergedDuplicatesSets,
        directors,
        ['countManual', 'countAuto', 'countUndone'],
      ),
      queuedDuplicateSets: foldSearchMerge(
        metrics.queuedDuplicateSets,
        directors,
        ['countManual', 'countAuto', 'countUndo'],
      ),
      mergeErrors: foldSearchMerge(metrics.mergeErrors, directors, ['count']),
      undoMergeErrors: foldSearchMerge(metrics.undoMergeErrors, directors, [
        'count',
      ]),
    }
  })

  const mergedDuplicateSetsChart = computed(() => {
    if (!issuesMetadata.value || !issuesMetrics.value) {
      return null
    }

    const hasSomeAutomaticMerge = dupeSetCharts.value.mergedDuplicatesSets.some(
      item => item.countAuto > 0,
    )
    const hasSomeUndoneMerge = dupeSetCharts.value.mergedDuplicatesSets.some(
      item => item.countUndone > 0,
    )

    if (hasSomeAutomaticMerge || hasSomeUndoneMerge) {
      return dupeSetCharts.value.mergedDuplicatesSets.map(item => {
        let hasWarning = false
        const director = validateDirector(item.director)
        if (director._isFallback) hasWarning = true
        const objects = directorObjects(director)
        if (objects.some(obj => obj._isFallback)) hasWarning = true

        // Search & Merge never auto-merges, so its chart drops that bar
        // rather than always showing a zero next to the two that can move.
        const bars = dupeSetChartBars(
          {
            manual: item.countManual,
            auto: item.countAuto,
            undo: item.countUndone,
            undoLabel: 'Undone',
          },
          TIER_COLORS,
          item.director === SEARCH_MERGE_CHART_KEY,
        )

        return {
          hasWarning,
          total: item.countManual + item.countAuto + item.countUndone,
          series: [
            {
              name: director.name,
              data: bars.data,
            },
          ],
          chartOptions: barChartOptions({
            title: director.name,
            categories: bars.categories,
            values: bars.data,
            unit: 'set',
            sub: director.name,
            colors: bars.colors,
          }),
        }
      })
    } else {
      const categories = []
      const names = []
      let hasWarning = false

      dupeSetCharts.value.mergedDuplicatesSets.forEach(item => {
        const director = validateDirector(item.director)
        if (director._isFallback) hasWarning = true
        categories.push(
          ruleLabel(
            director.name,
            dupeSetCharts.value.mergedDuplicatesSets.length,
          ),
        )
        names.push(director.name)
        const objects = directorObjects(director)
        if (objects.some(obj => obj._isFallback)) hasWarning = true
      })

      const data = dupeSetCharts.value.mergedDuplicatesSets.map(
        item => item.countManual,
      )

      return {
        hasWarning,
        series: [
          {
            name: 'Merged Duplicate Sets',
            data,
          },
        ],
        chartOptions: barChartOptions({
          categories,
          names,
          values: data,
          unit: 'set',
          colors: ruleColors(dupeSetCharts.value.mergedDuplicatesSets),
        }),
      }
    }
  })

  const queuedDuplicateSetsChart = computed(() => {
    if (!issuesMetadata.value || !issuesMetrics.value) {
      return null
    }

    const queuedData = dupeSetCharts.value.queuedDuplicateSets
    if (!queuedData || queuedData.length === 0) {
      return null
    }

    return queuedData.map(item => {
      let hasWarning = false
      const director = validateDirector(item.director)
      if (director._isFallback) hasWarning = true
      const objects = directorObjects(director)
      if (objects.some(obj => obj._isFallback)) hasWarning = true

      const bars = dupeSetChartBars(
        {
          manual: item.countManual,
          auto: item.countAuto,
          undo: item.countUndo,
          undoLabel: 'Undo',
        },
        TIER_COLORS,
        item.director === SEARCH_MERGE_CHART_KEY,
      )

      return {
        hasWarning,
        total: item.countManual + item.countAuto + item.countUndo,
        series: [
          {
            name: director.name,
            data: bars.data,
          },
        ],
        chartOptions: barChartOptions({
          title: director.name,
          categories: bars.categories,
          values: bars.data,
          unit: 'set',
          sub: director.name,
          colors: bars.colors,
        }),
      }
    })
  })

  const queuedDataIssuesChart = computed(() => {
    if (!issuesMetadata.value || !issuesMetrics.value) {
      return null
    }

    const queuedData = issuesMetrics.value.queuedDataIssues
    if (!queuedData || queuedData.length === 0) {
      return null
    }

    return queuedData.map(item => {
      let hasWarning = false
      const director = validateDirector(item.director)
      if (director._isFallback) hasWarning = true
      const data = [item.countManual, item.countAuto, item.countUndo]

      return {
        hasWarning,
        total: item.countManual + item.countAuto + item.countUndo,
        director: item.director,
        series: [
          {
            name: director.name,
            data,
          },
        ],
        chartOptions: barChartOptions({
          title: director.name,
          categories: ['Manual', 'Automatic', 'Undo'],
          values: data,
          unit: 'record',
          sub: director.name,
          colors: TIER_COLORS,
        }),
      }
    })
  })

  // narrower lines as bars get closer, so names stack instead of rotating
  function ruleLabel(name, barCount) {
    const width = Math.max(8, Math.min(16, Math.floor(72 / barCount)))
    return handleChartLabel(name, width)
  }

  // maxLineLength 0 puts each word on its own line
  function handleChartLabel(originalLabel, maxLineLength = 16) {
    if (!originalLabel) return ''
    if (originalLabel.length <= maxLineLength) {
      return originalLabel
    }

    const lines = []
    let currentLine = ''

    for (const word of originalLabel.split(' ')) {
      const candidate = currentLine ? `${currentLine} ${word}` : word
      if (!currentLine || candidate.length <= maxLineLength) {
        currentLine = candidate
      } else {
        lines.push(currentLine)
        currentLine = word
      }
    }

    if (currentLine) lines.push(currentLine)
    return lines
  }

  const mergeErrorsChart = computed(() => {
    if (!issuesMetadata.value || !issuesMetrics.value) {
      return null
    }

    const categories = []
    const names = []
    let hasWarning = false

    dupeSetCharts.value.mergeErrors.forEach(item => {
      const director = validateDirector(item.director)
      if (director._isFallback) hasWarning = true
      categories.push(
        ruleLabel(director.name, dupeSetCharts.value.mergeErrors.length),
      )
      names.push(director.name)
      const objects = directorObjects(director)
      if (objects.some(obj => obj._isFallback)) hasWarning = true
    })

    const data = dupeSetCharts.value.mergeErrors.map(item => item.count)

    return {
      hasWarning,
      series: [
        {
          name: 'Merge Errors',
          data,
        },
      ],
      chartOptions: barChartOptions({
        categories,
        names,
        values: data,
        unit: 'error',
        colors: ruleColors(dupeSetCharts.value.mergeErrors),
      }),
    }
  })

  const undoMergeErrorsChart = computed(() => {
    if (!issuesMetadata.value || !issuesMetrics.value) {
      return null
    }

    const categories = []
    const names = []
    let hasWarning = false

    dupeSetCharts.value.undoMergeErrors.forEach(item => {
      const director = validateDirector(item.director)
      if (director._isFallback) hasWarning = true
      categories.push(
        ruleLabel(director.name, dupeSetCharts.value.undoMergeErrors.length),
      )
      names.push(director.name)
      const objects = directorObjects(director)
      if (objects.some(obj => obj._isFallback)) hasWarning = true
    })

    const data = dupeSetCharts.value.undoMergeErrors.map(item => item.count)

    return {
      hasWarning,
      series: [
        {
          name: 'Merge Errors',
          data,
        },
      ],
      chartOptions: barChartOptions({
        categories,
        names,
        values: data,
        unit: 'error',
        colors: ruleColors(dupeSetCharts.value.undoMergeErrors),
      }),
    }
  })

  const issuesByRuleCharts = computed(() => {
    if (!issuesMetadata.value || !issuesMetrics.value) {
      return []
    }

    return issuesMetrics.value.dataToUpdateByRule
      .filter(rule => rule.issues.length > 0)
      .map(rule => {
        let hasWarning = false
        const directorMeta = validateDirector(rule.director)
        if (directorMeta._isFallback) hasWarning = true

        const issuesMeta = rule.issues.map(issue => {
          const meta = validateIssueType(issue.issue)
          if (meta._isFallback) hasWarning = true
          return meta
        })

        const data = rule.issues.map(issue => issue.count)

        return {
          hasWarning,
          director: rule.director,
          recordsWithIssues: rule.recordsWithIssues,
          series: [
            {
              name: directorMeta.name,
              data,
            },
          ],
          chartOptions: barChartOptions({
            title: directorMeta.name,
            // short phrases in half-width cards, so one word per line
            categories: issuesMeta.map(m => handleChartLabel(m.label, 0)),
            names: issuesMeta.map(m => m.label),
            values: data,
            unit: 'issue',
            sub: directorMeta.name,
            colors: issuesMeta.map(m => m.color),
          }),
        }
      })
  })

  const symptomCharts = computed(() => {
    if (!issuesMetadata.value || !issuesMetadata.value.issueTypes) {
      return []
    }

    if (
      issuesMetrics.value &&
      issuesMetrics.value.dataToUpdateBySymptom.some(
        el => el.objects.length > 0,
      )
    ) {
      return issuesMetrics.value.dataToUpdateBySymptom.map(issue => {
        let hasWarning = false
        const issueMeta = validateIssueType(issue.issue)
        if (issueMeta._isFallback) hasWarning = true

        const objectsMeta = issue.objects.map(object => {
          const meta = validateObject(object.object)
          if (meta._isFallback) hasWarning = true
          return meta
        })

        const data = issue.objects.map(object => object.count)

        return {
          hasWarning,
          issue: issue.issue,
          series: [
            {
              name: issueMeta.label,
              data,
            },
          ],
          chartOptions: barChartOptions({
            title: issueMeta.label,
            categories: objectsMeta.map(m => handleChartLabel(m.label, 0)),
            names: objectsMeta.map(m => m.label),
            values: data,
            unit: 'issue',
            sub: issueMeta.label,
            colors: barColors(
              issue.objects.map(object => object.object),
              objectSlots.value,
            ),
          }),
        }
      })
    } else {
      return Array(Object.keys(issuesMetadata.value.issueTypes).length).fill(
        null,
      )
    }
  })

  const fixActionsChart = computed(() => {
    if (!issuesMetrics.value || !issuesMetadata.value) {
      return null
    }

    const hasSomeAutomaticFix = issuesMetrics.value.fixedIssues.some(
      item => item.countAuto > 0,
    )
    const hasSomeUndoneFix = issuesMetrics.value.fixedIssues.some(
      item => item.countUndone > 0,
    )

    if (hasSomeAutomaticFix || hasSomeUndoneFix) {
      return issuesMetrics.value.fixedIssues.map(item => {
        const directorMeta = validateDirector(item.director)
        const hasWarning = !!directorMeta._isFallback
        const data = [item.countManual, item.countAuto, item.countUndone]

        return {
          hasWarning,
          total: item.countManual + item.countAuto + item.countUndone,
          director: item.director,
          series: [
            {
              name: directorMeta.name,
              data,
            },
          ],
          chartOptions: barChartOptions({
            title: directorMeta.name,
            categories: ['Manual', 'Automatic', 'Undone'],
            values: data,
            unit: 'fix',
            sub: directorMeta.name,
            colors: TIER_COLORS,
          }),
        }
      })
    } else {
      let hasWarning = false
      const directorsMeta = issuesMetrics.value.fixedIssues.map(item => {
        const meta = validateDirector(item.director)
        if (meta._isFallback) hasWarning = true
        return meta
      })

      const colors = ruleColors(issuesMetrics.value.fixedIssues)
      const data = issuesMetrics.value.fixedIssues.map(item => item.countManual)

      return {
        hasWarning,
        series: [
          {
            name: 'Fix Actions',
            data,
          },
        ],
        chartOptions: barChartOptions({
          categories: directorsMeta.map(m =>
            ruleLabel(m.name, directorsMeta.length),
          ),
          names: directorsMeta.map(m => m.name),
          values: data,
          unit: 'fix',
          colors,
        }),
      }
    }
  })

  const fixErrorsChart = computed(() => {
    if (!issuesMetrics.value || !issuesMetadata.value) {
      return null
    }

    let hasWarning = false
    const directorsMeta = issuesMetrics.value.fixErrors.map(item => {
      const meta = validateDirector(item.director)
      if (meta._isFallback) hasWarning = true
      return meta
    })

    const colors = ruleColors(issuesMetrics.value.fixErrors)
    const data = issuesMetrics.value.fixErrors.map(item => item.count)

    return {
      hasWarning,
      series: [
        {
          name: 'Fix Errors',
          data,
        },
      ],
      chartOptions: barChartOptions({
        categories: directorsMeta.map(m =>
          ruleLabel(m.name, directorsMeta.length),
        ),
        names: directorsMeta.map(m => m.name),
        values: data,
        unit: 'error',
        colors,
      }),
    }
  })

  const undoFixErrorsChart = computed(() => {
    if (!issuesMetrics.value || !issuesMetadata.value) {
      return null
    }

    let hasWarning = false
    const directorsMeta = issuesMetrics.value.undoFixErrors.map(item => {
      const meta = validateDirector(item.director)
      if (meta._isFallback) hasWarning = true
      return meta
    })

    const colors = ruleColors(issuesMetrics.value.undoFixErrors)
    const data = issuesMetrics.value.undoFixErrors.map(item => item.count)

    return {
      hasWarning,
      series: [
        {
          name: 'Undo Fix Errors',
          data,
        },
      ],
      chartOptions: barChartOptions({
        categories: directorsMeta.map(m =>
          ruleLabel(m.name, directorsMeta.length),
        ),
        names: directorsMeta.map(m => m.name),
        values: data,
        unit: 'error',
        colors,
      }),
    }
  })

  const gridTotals = computed(
    () =>
      issuesMetrics.value?.gridTotals ?? {
        uniqueRecords: 0,
        totalIssues: 0,
      },
  )

  async function getIssuesStats() {
    isLoadingIssuesStats.value = true
    isIssuesStatsError.value = false
    databaseIsClean.value = false

    try {
      issuesMetrics.value = await getMetrics()

      // A duplicates-only org gets no Data Issues data at all, which would
      // otherwise read as a vacuously clean database
      if (
        sessionStore.isDataIssuesEnabled &&
        !issuesMetrics.value.dataToUpdateByRule.some(el => el.issues.length > 0)
      ) {
        databaseIsClean.value = true
      }

      duplicatesCount.value = issuesMetrics.value.duplicateSetsToReview.reduce(
        (acc, item) => acc + item.count,
        0,
      )

      duplicateRecordsCount.value =
        issuesMetrics.value.duplicateSetsToReview.reduce(
          (acc, item) => acc + (item.recordCount || 0),
          0,
        )

      mergedDuplicatesCount.value =
        issuesMetrics.value.mergedDuplicatesSets.reduce(
          (acc, item) =>
            acc + item.countManual + item.countAuto + item.countUndone,
          0,
        )

      queuedDuplicatesCount.value =
        issuesMetrics.value.queuedDuplicateSets?.reduce(
          (acc, item) =>
            acc + item.countManual + item.countAuto + item.countUndo,
          0,
        ) || 0

      queuedDataIssuesCount.value =
        issuesMetrics.value.queuedDataIssues?.reduce(
          (acc, item) =>
            acc + item.countManual + item.countAuto + item.countUndo,
          0,
        ) || 0

      queuedLastUpdated.value = issuesMetrics.value.lastGenerateTime
        ? new Date(issuesMetrics.value.lastGenerateTime)
        : null

      mergeErrorsCount.value = issuesMetrics.value.mergeErrors.reduce(
        (acc, item) => acc + item.count,
        0,
      )

      undoMergeErrorsCount.value = issuesMetrics.value.undoMergeErrors.reduce(
        (acc, item) => acc + item.count,
        0,
      )

      fixedRecordsCount.value = issuesMetrics.value.fixedIssues.reduce(
        (acc, item) => acc + item.countManual + item.countAuto,
        0,
      )

      fixErrorsCount.value = issuesMetrics.value.fixErrors.reduce(
        (acc, item) => acc + item.count,
        0,
      )

      undoFixErrorsCount.value = issuesMetrics.value.undoFixErrors.reduce(
        (acc, item) => acc + item.count,
        0,
      )
    } catch (error) {
      isIssuesStatsError.value = true
      throw error
    } finally {
      isLoadingIssuesStats.value = false
    }
  }

  function startQueuedPolling() {
    stopQueuedPolling()
    queuedPollingInterval = setInterval(() => {
      getIssuesStats()
    }, 60000)
  }

  function stopQueuedPolling() {
    if (queuedPollingInterval) {
      clearInterval(queuedPollingInterval)
      queuedPollingInterval = null
    }
  }

  return {
    overviewChart,

    issuesMetadata,
    isLoadingMetadata,
    isMetadataError,

    databaseIsClean,
    issuesMetrics,
    isLoadingIssuesStats,
    isIssuesStatsError,
    symptomCharts,
    issuesByRuleCharts,
    gridTotals,

    fixActionsChart,
    fixedRecordsCountAnimated,

    fixErrorsChart,
    fixErrorsCountAnimated,

    undoFixErrorsChart,
    undoFixErrorsCountAnimated,

    duplicateSetsChart,
    duplicateRuleList,
    hasDuplicateRuleList,
    hasDuplicateData,
    duplicatesCountAnimated,
    duplicateRecordsCountAnimated,
    duplicateRecordsPercentage,
    hasDuplicateRecordCounts,

    mergedDuplicateSetsChart,
    mergedDuplicatesCountAnimated,

    queuedDuplicateSetsChart,
    queuedDuplicatesCount,
    queuedDuplicatesCountAnimated,

    queuedDataIssuesChart,
    queuedDataIssuesCount,
    queuedDataIssuesCountAnimated,

    mergeErrorsChart,
    mergeErrorsCountAnimated,

    undoMergeErrorsChart,
    undoMergeErrorsCountAnimated,

    getMetadata,
    reloadMetadataForNewDirectors,
    getIssuesStats,

    queuedLastUpdated,
    startQueuedPolling,
    stopQueuedPolling,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useStats, import.meta.hot))
}
